import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListMerchantData } from 'reactRedux/thunks/merchnatThunk/merchantThunk';
import PropTypes from 'prop-types';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import { useDebounce } from 'hooks/useDebounce';
import useRegions from 'hooks/useRegions';

const MerchantListModal = ({
  isOpen,
  onClose,
  setSelectedMerchants,
  isRemove,
  setIsRemove,
  setSelectedMerchantName,
  setSelectedMerchantObjects,
  setValue,
  roleId,
  currentSelectedMerchants,
  currentSelectedMerchantNames,
  selectedRegion,
  setSelectedRegion,
  sellerId,
  adminDetails,
  selfAssign,
  forceCheckbox = false,
  forceRegionFilter = false, // New prop for coupon management
  includeFirstOrderDate = false,
  showMerchantId = false, // Show a Merchant ID column (station assign flow)
  showCountry = false, // Show a Country column (station assign flow)
  hideLocationAndAddress = false, // Hide Location Id / Address columns
  singleSelectCheckbox = false, // Checkbox UI but single-select (station assign flow)
}) => {
  const { regions } = useRegions();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]); // For multiple selection
  const [selectedMerchantNames, setSelectedMerchantNames] = useState([]); // For multiple names
  // Cache of merchant objects seen while paginating so cross-page selections
  // keep fields like firstOrderDate even after the page has changed.
  const [merchantObjectsById, setMerchantObjectsById] = useState({});
  const dispatch = useDispatch();
  const { isLoading, merchantListData } = useSelector(
    (state) => state.MerchantSlice,
  );
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    dispatch(
      ListMerchantData({
        page: page,
        limit: limit,
        search: debouncedSearch,
        ...((roleId === 4 || roleId === 3) && {
          forRole: roleId,
          regionId: selfAssign ? adminDetails?.regionId : selectedRegion,
          sellerId,
          selfAssign: !!selfAssign,
        }),
        // For coupon management: filter by region when forceRegionFilter is true
        ...(forceRegionFilter &&
          selectedRegion && {
            regionId: selectedRegion,
          }),
        ...(includeFirstOrderDate && { firstOrderDate: true }),
      }),
    );
  }, [
    page,
    limit,
    dispatch,
    debouncedSearch,
    roleId,
    selectedRegion,
    sellerId,
    forceRegionFilter,
    selfAssign,
    adminDetails,
    includeFirstOrderDate,
  ]);

  useEffect(() => {
    if (!merchantListData?.list?.length) return;
    setMerchantObjectsById((prev) => {
      const next = { ...prev };
      merchantListData.list.forEach((m) => {
        if (m?.merchantId !== undefined) {
          next[m.merchantId] = m;
        }
      });
      return next;
    });
  }, [merchantListData]);

  const handleRadioChange = (id) => {
    setSelectedMerchantId(id);
    const selectedMerchant = merchantListData?.list.find(
      (merchant) => merchant.merchantId === id,
    );
    // Set the selected merchant's name
    if (selectedMerchant) {
      setSelectedMerchantName(selectedMerchant.name);
    }
  };

  //////////// For multiple checked functionality /////////
  const handleCheckboxChange = (id, name) => {
    setSelectedMerchantIds((prev) => {
      if (prev.includes(id)) {
        // Remove if already selected
        return prev.filter((item) => item !== id);
      } else {
        // Add if not selected
        return [...prev, id];
      }
    });

    setSelectedMerchantNames((prev) => {
      if (prev.includes(name)) {
        return prev.filter((item) => item !== name);
      } else {
        return [...prev, name];
      }
    });
  };

  useEffect(() => {
    if (isRemove) {
      setSelectedMerchantId('');
      setSelectedMerchantName('');
      setIsRemove(false);
    }
  }, [isRemove, setIsRemove, setSelectedMerchantName]);

  const handleRegionSelect = (event) => {
    const selectedRegionId = event.target.value;
    setSelectedRegion(selectedRegionId);
  };

  const handleSelectMerchant = () => {
    setSearch('');
    // Use checkbox logic if forceCheckbox is true or if roleId is 3 or 4
    if (forceCheckbox || roleId === 4 || roleId === 3) {
      // Store full merchant objects for the selected merchants. Pull from
      // the cross-page cache so selections made on earlier pages still
      // carry their full payload (e.g. firstOrderDate) into the parent.
      const selectedMerchantObjects = selectedMerchantIds
        .map(
          (id) =>
            merchantObjectsById[id] ||
            merchantListData?.list.find(
              (merchant) => merchant.merchantId === id,
            ),
        )
        .filter(Boolean);

      setSelectedMerchants(selectedMerchantIds, selectedMerchantObjects);
      setSelectedMerchantName(selectedMerchantNames.join('||'));
      setValue('merchantId', selectedMerchantIds);

      setSelectedMerchantObjects &&
        setSelectedMerchantObjects(selectedMerchantObjects);

      setSelectedRegion(selectedRegion);
      setPage(1);
    } else {
      setSelectedMerchants(selectedMerchantId);
      setValue('merchantId', selectedMerchantId);
      setSelectedRegion(selectedRegion);
      setPage(1);
    }
    onClose();
  };

  const onModalCloseIcon = () => {
    setSelectedMerchantId('');
    setSelectedMerchantIds([]);
    setSelectedMerchantNames([]);
    setMerchantObjectsById({});
    setIsRemove(false);
    onClose();
    setPage(1);
    setSearch('');
    if (sellerId) {
      setSelectedMerchantId(sellerId);
    } else {
      setSelectedRegion('1');
    }
  };

  // Initialize state with current selected merchants
  useEffect(() => {
    // Use checkbox logic if forceCheckbox is true or if roleId is 3 or 4
    if (forceCheckbox || roleId === 4 || roleId === 3) {
      if (currentSelectedMerchants && currentSelectedMerchantNames) {
        setSelectedMerchantIds(currentSelectedMerchants);
        setSelectedMerchantNames(
          typeof currentSelectedMerchantNames === 'string'
            ? currentSelectedMerchantNames.split('||')
            : currentSelectedMerchantNames,
        );
      } else {
        setSelectedMerchantIds([]);
        setSelectedMerchantNames([]);
        setSelectedMerchantId('');
      }
    }
  }, [
    isOpen,
    currentSelectedMerchants,
    currentSelectedMerchantNames,
    roleId,
    forceCheckbox,
  ]);

  useEffect(() => {
    if (sellerId && selectedRegion) {
      setValue('regionId', selectedRegion);
    }
  }, [sellerId, selectedRegion, setValue]);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.merchantId, {
      id: 'selectMerchant',
      header: () => (
        <Text
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        ></Text>
      ),
      cell: (info) => {
        const merchantId = info.getValue();
        const merchantName = info.row.original.name;
        const isSelected = selectedMerchantIds.includes(merchantId);

        // Station assign flow: checkbox UI but single-select. Picking a new
        // merchant replaces the previous one (assign API updates it).
        if (singleSelectCheckbox) {
          return (
            <Checkbox
              isChecked={selectedMerchantId === merchantId}
              onChange={() => handleRadioChange(merchantId, merchantName)}
            />
          );
        }

        // Use checkbox if forceCheckbox is true, otherwise follow role logic
        return forceCheckbox || roleId === 4 || roleId === 3 ? (
          <Checkbox
            isChecked={isSelected}
            onChange={() => handleCheckboxChange(merchantId, merchantName)}
          />
        ) : (
          <Radio
            value={merchantId}
            onChange={() => handleRadioChange(merchantId, merchantName)}
            isChecked={selectedMerchantId === merchantId}
          />
        );
      },
    }),
    ...(showMerchantId
      ? [
          columnHelper.accessor((row) => row?.merchantId, {
            id: 'merchantId',
            header: () => (
              <Text
                justifyContent="space-between"
                align="center"
                fontSize={{ sm: '10px', lg: '12px' }}
                color="gray.400"
                whiteSpace="nowrap"
              >
                Merchant ID
              </Text>
            ),
            cell: (info) => (
              <Flex align="center">
                <Text color={textColor} fontSize="sm" fontWeight="600">
                  {info.getValue() ?? 'N/A'}
                </Text>
              </Flex>
            ),
          }),
        ]
      : []),
    columnHelper.accessor((row) => row?.name, {
      id: 'name',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Merchant
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          {/* <Radio
            value={info.getValue()}
            onChange={() => handleRadioChange(info.getValue())}
            isChecked={selectedMerchantId === info.getValue()}
          /> */}
          <Text color={textColor} fontSize="sm" fontWeight="600" ml={2}>
            {info.getValue() ? info.getValue() : 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    ...(hideLocationAndAddress
      ? []
      : [
          columnHelper.accessor((row) => row?.locationId, {
            id: 'locationId',
            header: () => (
              <Text
                justifyContent="space-between"
                align="center"
                fontSize={{ sm: '10px', lg: '12px' }}
                color="gray.400"
                whiteSpace="nowrap"
              >
                Location Id
              </Text>
            ),
            cell: (info) => (
              <Flex align="center">
                <Text color={textColor} fontSize="sm" fontWeight="600">
                  {info.getValue()}
                </Text>
              </Flex>
            ),
          }),
          columnHelper.accessor((row) => row?.address, {
            id: 'address',
            header: () => (
              <Text
                justifyContent="space-between"
                align="center"
                fontSize={{ sm: '10px', lg: '12px' }}
                color="gray.400"
                whiteSpace="nowrap"
              >
                Address
              </Text>
            ),
            cell: (info) => (
              <Flex align="center">
                <Text color={textColor} fontSize="sm" fontWeight="600">
                  {info.getValue()}
                </Text>
              </Flex>
            ),
          }),
        ]),
    ...(showCountry
      ? [
          columnHelper.accessor((row) => row?.country, {
            id: 'country',
            header: () => (
              <Text
                justifyContent="space-between"
                align="center"
                fontSize={{ sm: '10px', lg: '12px' }}
                color="gray.400"
                whiteSpace="nowrap"
              >
                Country
              </Text>
            ),
            cell: (info) => (
              <Flex align="center">
                <Text color={textColor} fontSize="sm" fontWeight="600">
                  {info.getValue() ? info.getValue() : 'N/A'}
                </Text>
              </Flex>
            ),
          }),
        ]
      : []),
  ];

  return (
    <Box>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader position={'relative'}>
            <Flex
              justify="space-between"
              align="center"
              width="100%"
              paddingRight="48px"
              flexWrap={'wrap'}
              gap={4}
            >
              {/* Modal Title */}
              <Text fontSize="xl" fontWeight="bold" whiteSpace="nowrap">
                Merchant List
              </Text>

              {/* Region Select */}
              <Flex align="center" gap={4} flexWrap={'wrap'}>
                {(roleId === 4 || roleId === 3) && adminDetails.role === 1 && (
                  <Select
                    bg="white"
                    borderColor="reviveGray"
                    color="#292D32"
                    fontWeight="semibold"
                    borderRadius="8px"
                    onChange={handleRegionSelect}
                    value={selectedRegion}
                    width="200px"
                    disabled={!!sellerId}
                    _disabled={{
                      opacity: 0.6,
                      cursor: 'not-allowed',
                      borderColor: 'gray.200',
                    }}
                  >
                    {regions?.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.label}
                      </option>
                    ))}
                  </Select>
                )}

                {/* Search Bar */}
                <Box width="250px">
                  <CommonSearch
                    search={search}
                    setSearch={setSearch}
                    setPage={setPage}
                  />
                </Box>
              </Flex>

              {/* Close button */}
              <ModalCloseButton
                position={'absolute'}
                right={{ base: '16px', md: '32px' }}
                top={{ base: '16px', md: '21px' }}
                onClick={() => onModalCloseIcon()}
              />
            </Flex>
          </ModalHeader>
          <ModalBody>
            <CommonTable
              columns={columns}
              data={merchantListData?.list}
              isLoading={isLoading}
              keyValue="id"
              rowclick={['actions', 'status']}
              excludes={['actions', 'status']}
              isMerchantList={true}
              dynamicWidth={
                showMerchantId
                  ? {
                      // Percentages keep the checkbox column tight so it sits
                      // right next to the Merchant ID column.
                      selectMerchant: '5%',
                      merchantId: '22%',
                      name: '43%',
                      country: '30%',
                    }
                  : {
                      selectMerchant: '10px',
                      name: '50px',
                      locationId: '50px',
                      address: '190px',
                    }
              }
            />
            {/* {merchantListData?.total > 10 && ( */}
            <CommonPagination
              totalCount={merchantListData?.total || 0}
              activePage={page}
              pageCount={Math.ceil(
                (merchantListData?.total || 0) /
                  (merchantListData?.limit || 10),
              )}
              onPageChange={(e) => setPage(e)}
              itemsPerPage={merchantListData?.limit || 10}
              onItemsPerPageChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />

            {/* )} */}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSelectMerchant}
              isDisabled={
                forceCheckbox || roleId === 4 || roleId === 3
                  ? selectedMerchantIds.length === 0
                  : !selectedMerchantId
              }
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

MerchantListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setSelectedMerchants: PropTypes.func.isRequired,
  isRemove: PropTypes.bool.isRequired,
  setIsRemove: PropTypes.func.isRequired,
  setSelectedMerchantName: PropTypes.func.isRequired,
  setSelectedMerchantObjects: PropTypes.func,
  setValue: PropTypes.func,
  roleId: PropTypes.number,
  currentSelectedMerchants: PropTypes.array,
  currentSelectedMerchantNames: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
  ]),
  selectedRegion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedRegion: PropTypes.func,
  sellerId: PropTypes.number,
  adminDetails: PropTypes.object,
  selfAssign: PropTypes.string,
  forceCheckbox: PropTypes.bool,
  forceRegionFilter: PropTypes.bool,
  includeFirstOrderDate: PropTypes.bool,
  showMerchantId: PropTypes.bool,
  showCountry: PropTypes.bool,
  hideLocationAndAddress: PropTypes.bool,
  singleSelectCheckbox: PropTypes.bool,
};

export default MerchantListModal;
